/**
 * A holder's last sign-in, in words that are true (ORDR-120, FR-STF-003).
 *
 * THE OBVIOUS LABEL IS FALSE. The field is `last_activity_at`, and "Last
 * activity" is what a column would naturally be called. But paio-auth computes
 * it as the latest `auth_session.login_at` among the holder's RETAINED sessions
 * (`access/roster.py`, `ACTIVITY_SOURCE = "session_sign_in"`): it does not move
 * while someone works in an open session. So the label is "Last sign-in".
 *
 * AND IT FORGETS. Sessions are capped at 30 days and a sign-out erases one, so
 * `null` is "No sign-in on record", never "never signed in", and somebody who
 * signed in 31 days ago reads `null`. The panel carries one line saying so.
 *
 * A DIFFERENT SOURCE IS A DIFFERENT FACT. The payload names its source so a
 * client can refuse to relabel one it does not know; this module does, and
 * renders "Last sign-in unavailable" rather than calling it a sign-in.
 */

import type { RosterHolder } from "./types.js";

/** Past this, a row says so in text. A UI constant: FR-STF-005 is out of scope. */
export const LONG_ABSENCE_DAYS = 14;

/** The only `activity_source` whose timestamp is a sign-in. */
const SIGN_IN_SOURCE = "session_sign_in";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export interface SignInFormat {
  /** The clock. Injected so tests do not depend on when they run. */
  readonly now: Date;
  /** Undefined means the viewer's own locale and time zone. */
  readonly locale?: string;
  readonly timeZone?: string;
}

export interface SignInView {
  /** The whole "Last sign-in" phrase for one row. */
  readonly text: string;
  /** "Not signed in for over 14 days", or null when that is not known to be true. */
  readonly absence: string | null;
}

function age(elapsed: number, locale: string | undefined): string {
  const words = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
  if (elapsed < HOUR) {
    return words.format(-Math.floor(elapsed / MINUTE), "minute");
  }
  if (elapsed < DAY) {
    return words.format(-Math.floor(elapsed / HOUR), "hour");
  }
  return words.format(-Math.floor(elapsed / DAY), "day");
}

export function describeSignIn(holder: RosterHolder | null, format: SignInFormat): SignInView {
  if (holder === null || holder.activity_source !== SIGN_IN_SOURCE) {
    return { text: "Last sign-in unavailable", absence: null };
  }
  if (holder.last_activity_at === null) {
    // The text itself is the at-a-glance marker for this row.
    return { text: "Last sign-in: No sign-in on record", absence: null };
  }
  const at = new Date(holder.last_activity_at);
  if (Number.isNaN(at.getTime())) {
    return { text: "Last sign-in unavailable", absence: null };
  }
  const when = new Intl.DateTimeFormat(format.locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: format.timeZone,
  }).format(at);
  // A clock slightly behind the server's must not read as "in 2 minutes".
  const elapsed = Math.max(0, format.now.getTime() - at.getTime());
  return {
    text: `Last sign-in: ${when} (${age(elapsed, format.locale)})`,
    absence:
      elapsed > LONG_ABSENCE_DAYS * DAY
        ? `Not signed in for over ${String(LONG_ABSENCE_DAYS)} days`
        : null,
  };
}
